import React, { useState } from 'react';
import {
  Box, Button, Paper, Typography, Chip, CircularProgress,
  Alert, Divider, Collapse, IconButton
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { callClaude } from '../utils/claudeProxy';

const FIELD_LABELS = {
  competencies: 'Kompetensområden',
  standards: 'Standarder och Regelverk',
  industries: 'Branscherfarenhet',
  personal_profile: 'Personlig profil',
  tools_expert: 'IT-verktyg (Mycket goda)',
  tools_good: 'IT-verktyg (Goda)',
  tools_basic: 'IT-verktyg (Grundläggande)',
  languages: 'Språk',
};

async function fetchExistingTags() {
  try {
    const snap = await getDocs(collection(db, 'profiles'));
    const competencies = new Set();
    const standards = new Set();
    const industries = new Set();
    const tools = new Set();
    snap.docs.forEach(d => {
      const data = d.data();
      (data.competencies || []).forEach(t => competencies.add(t));
      (data.standards || []).forEach(t => standards.add(t));
      (data.industries || []).forEach(t => industries.add(t));
      (data.tools?.expert || []).forEach(t => tools.add(t));
      (data.tools?.good || []).forEach(t => tools.add(t));
      (data.tools?.basic || []).forEach(t => tools.add(t));
    });
    return {
      competencies: [...competencies],
      standards: [...standards],
      industries: [...industries],
      tools: [...tools],
    };
  } catch {
    return {
      competencies: [], standards: [],
      industries: [], tools: []
    };
  }
}

function buildTagInstruction(tags) {
  return `
VIKTIG INSTRUKTION FÖR TAGGAR:
Använd EXAKT samma stavning och skiftläge som i dessa befintliga taggar om de passar:
- Kompetenser: ${tags.competencies.slice(0, 40).join(', ') || 'inga ännu'}
- Standarder: ${tags.standards.slice(0, 30).join(', ') || 'inga ännu'}
- Branscher: ${tags.industries.slice(0, 30).join(', ') || 'inga ännu'}
- Verktyg: ${tags.tools.slice(0, 40).join(', ') || 'inga ännu'}

Skapa bara NYA taggar om inget befintligt alternativ passar.
Normalisera alltid: använd gemener först (t.ex. "BIM-projektering" inte
"bim-projektering"), behåll branschspecifika förkortningar i versaler
(ISO, BIM, CAD, IFC etc).`;
}

export default function AIProfileEvaluator({
  profile, onAccept, tenderContext
}) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [accepted, setAccepted] = useState({});

  async function handleEvaluate() {
    setLoading(true);
    setError('');
    setSuggestions(null);
    setAccepted({});
    try {
      const tags = await fetchExistingTags();
      const tagInstruction = buildTagInstruction(tags);

      const data = await callClaude({
        model: 'claude-opus-5',
        max_tokens: 3000,
        messages: [{
          role: 'user',
          content: `Du är en expert på att analysera konsult-CV:n inom bygg- och anläggningsbranschen.

Analysera denna konsultprofil och föreslå kompletteringar av tagg-fält baserat på vad som framgår av referensuppdragen och övrig information.
${tenderContext
  ? '\nANBUDSKONTEXT:\n' + tenderContext +
    '\n\nFöreslå taggar relevanta både för profilen och för detta anbud.'
  : ''}

${tagInstruction}

NUVARANDE PROFIL:
${JSON.stringify({
  assignments: profile.assignments,
  employment: profile.employment,
  competencies: profile.competencies,
  standards: profile.standards,
  industries: profile.industries,
  personal_profile: profile.personal_profile,
  tools: profile.tools,
  languages: profile.languages,
  ingress: profile.ingress
}, null, 2)}

Returnera ENDAST ett JSON-objekt med förslag på NYA taggar
som INTE redan finns i profilen.
Föreslå bara taggar som tydligt framgår av profilen.
{
  "competencies": [],
  "standards": [],
  "industries": [],
  "personal_profile": [],
  "tools_expert": [],
  "tools_good": [],
  "tools_basic": [],
  "languages": [],
  "summary": "Kort motivering pa svenska"
}`
        }]
      });
      const raw = data.content[0].text;
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      setSuggestions(parsed);
    } catch (err) {
      console.error(err);
      setError('Kunde inte utvärdera profilen. Försök igen.');
    } finally {
      setLoading(false);
    }
  }

  function handleAccept(field, value) {
    const key = field + '_' + value;
    setAccepted(prev => ({ ...prev, [key]: true }));
    onAccept(field, value);
  }

  function handleAcceptAll(field, values) {
    values.forEach(value => handleAccept(field, value));
  }

  const totalSuggestions = suggestions
    ? Object.entries(suggestions)
        .filter(([k]) => k !== 'summary')
        .reduce((sum, [, v]) =>
          sum + (Array.isArray(v) ? v.length : 0), 0)
    : 0;

  const acceptedCount = Object.keys(accepted).length;

  return (
    <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0',
      borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AutoFixHighIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              AI-utvärdering av profil
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tenderContext
                ? 'Låt AI analysera profilen och föreslå kompletteringar för detta anbud'
                : 'Låt AI analysera dina uppdrag och föreslå kompletteringar'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {suggestions && (
            <IconButton size="small"
              onClick={() => setExpanded(prev => !prev)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <Button variant="contained"
            startIcon={loading
              ? <CircularProgress size={16} color="inherit" />
              : <AutoFixHighIcon />}
            onClick={handleEvaluate}
            disabled={loading}
            sx={{ bgcolor: '#FF6B00', color: 'white',
              '&:hover': { bgcolor: '#e65c00' } }}>
            {loading
              ? 'Analyserar...'
              : suggestions ? 'Analysera igen' : 'Analysera profil'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      {suggestions && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 3 }}>
            {suggestions.summary && (
              <Box sx={{ bgcolor: '#f5f5f5', p: 2,
                borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {suggestions.summary}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {totalSuggestions} förslag – {acceptedCount} accepterade
              </Typography>
              {acceptedCount < totalSuggestions && (
                <Button size="small" variant="outlined"
                  onClick={() => {
                    Object.entries(suggestions)
                      .filter(([k]) => k !== 'summary')
                      .forEach(([field, values]) => {
                        if (Array.isArray(values)) {
                          handleAcceptAll(field,
                            values.filter(
                              v => !accepted[field + '_' + v]
                            )
                          );
                        }
                      });
                  }}>
                  Acceptera alla
                </Button>
              )}
            </Box>

            {Object.entries(suggestions)
              .filter(([key]) => key !== 'summary')
              .filter(([, values]) =>
                Array.isArray(values) && values.length > 0)
              .map(([field, values]) => (
                <Box key={field} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {FIELD_LABELS[field] || field}
                    </Typography>
                    <Button size="small" variant="text"
                      sx={{ color: '#FF6B00', fontSize: '0.7rem' }}
                      onClick={() => handleAcceptAll(field,
                        values.filter(
                          v => !accepted[field + '_' + v]
                        ))}>
                      Lägg till alla
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap',
                    gap: 1 }}>
                    {values.map((value, i) => {
                      const key = field + '_' + value;
                      const isAccepted = accepted[key];
                      return (
                        <Chip key={i} label={value} size="small"
                          variant={isAccepted ? 'filled' : 'outlined'}
                          icon={isAccepted ? undefined : <AddIcon />}
                          onClick={isAccepted
                            ? undefined
                            : () => handleAccept(field, value)}
                          sx={{
                            cursor: isAccepted ? 'default' : 'pointer',
                            bgcolor: isAccepted ? '#e8f5e9' : undefined,
                            borderColor: isAccepted
                              ? '#66bb6a' : '#FF6B00',
                            color: isAccepted ? '#2e7d32' : '#FF6B00',
                            '& .MuiChip-icon': { color: '#FF6B00' },
                            transition: 'all 0.2s',
                            '&:hover': isAccepted
                              ? {} : { bgcolor: '#fff3e0' }
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}

            {totalSuggestions === 0 && (
              <Alert severity="success">
                Profilen verkar vara komplett! Inga saknade
                taggar hittades.
              </Alert>
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}